import React, { useMemo, useState, useCallback, useEffect } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Skeleton from "@mui/material/Skeleton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Collapse from "@mui/material/Collapse";
import Chip from "@mui/material/Chip";
import { alpha } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import Divider from "@mui/material/Divider";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useQuery } from "@apollo/client";
import {
  COMPANY_INSTALL_LOCATIONS_BY_PRODUCT_ID_QUERY,
  COMPANY_INSTALLS_LOCATIONS_QUERY,
  CompanyInstallsPayload,
} from "../../../../../../legacy/queries/Installs";
import { CountryLogo } from "../../../../../../components/CountryLogo";
import { CompanyLogo } from "../../../../../../components/CompanyLogo";
import { HgProduct } from "../../../../../../types/taxonomy";
import { formatNumber } from "../../../../../../utils/numberUtils";
import { useCompanyPageStore } from "../../../../../../store/companyPageStore";
import Settings from "@mui/icons-material/Settings";
import { lightScrollbarStyle } from "../../../../../../styles/lightScrollbarStyle";
import { COMPANY_SEARCH_CLIENT } from "../../../../../../utils/consts";

interface InstallsLocationsDrawerProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  product: Pick<HgProduct, "id" | "name"> | null;
  productLogo: string;
}

interface StateData {
  stateName: string;
  cityName?: string;
  locations: number;
}

interface CountryData {
  countryCode: string;
  countryName: string;
  totalLocations: number;
  states: StateData[];
}

const StateListItem: React.FC<{
  state: StateData;
}> = React.memo(({ state }) => {
  const hasState = state.stateName && state.stateName.length > 0;
  const hasCity = state.cityName && state.cityName.length > 0;

  const displayName =
    !hasState && !hasCity
      ? "(country level)"
      : hasState && hasCity
        ? `${state.stateName}, ${state.cityName}`
        : state.stateName || state.cityName || "(country level)";

  return (
    <ListItem sx={{ height: 40 }}>
      <ListItemIcon sx={{ minWidth: 32 }}>
        <LocationOnIcon sx={{ fontSize: 24, color: "text.secondary" }} />
      </ListItemIcon>
      <ListItemText
        primary={<Typography variant="body1">{displayName}</Typography>}
      />
    </ListItem>
  );
});

StateListItem.displayName = "StateListItem";

const CountryListItem: React.FC<{
  country: CountryData;
  isExpanded: boolean;
  onToggle: () => void;
  visibleStatesCount: number;
  onShowMoreStates: () => void;
}> = React.memo(
  ({ country, isExpanded, onToggle, visibleStatesCount, onShowMoreStates }) => {
    const visibleStates = country.states.slice(0, visibleStatesCount);
    const hasMoreStates = country.states.length > visibleStatesCount;

    return (
      <>
        <ListItem disablePadding>
          <ListItemButton
            onClick={onToggle}
            sx={{
              py: 1.5,
              px: 2,
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <CountryLogo
                alpha2={country.countryCode}
                size={24}
                variant="rounded"
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body1">
                  {country.countryName} ({formatNumber(country.totalLocations)})
                </Typography>
              }
              sx={{ flex: 1 }}
            />
            {isExpanded ? (
              <ExpandLessIcon sx={{ color: "text.secondary" }} />
            ) : (
              <ExpandMoreIcon sx={{ color: "text.secondary" }} />
            )}
          </ListItemButton>
        </ListItem>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {visibleStates.map((state, index) => (
              <React.Fragment
                key={`${state.stateName}-${state.cityName}-${index}`}
              >
                <StateListItem state={state} />
                {index < visibleStates.length - 1 && <Divider />}
              </React.Fragment>
            ))}
            {hasMoreStates && (
              <ListItem sx={{ pl: 4, py: 1 }}>
                <Button
                  size="small"
                  onClick={onShowMoreStates}
                  sx={{
                    textTransform: "none",
                    color: "primary.main",
                    fontSize: "0.875rem",
                    ml: 0.5,
                  }}
                >
                  View more ({country.states.length - visibleStatesCount}{" "}
                  remaining)
                </Button>
              </ListItem>
            )}
          </List>
        </Collapse>
      </>
    );
  }
);

CountryListItem.displayName = "CountryListItem";

const LoadingSkeleton: React.FC = () => (
  <List>
    {Array.from({ length: 20 }).map((_, index) => (
      <ListItem key={index} disablePadding>
        <ListItemButton sx={{ py: 1.5, px: 2 }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Skeleton variant="rounded" width={24} height={24} />
          </ListItemIcon>
          <ListItemText primary={<Skeleton variant="text" width="70%" />} />
          <Skeleton variant="circular" width={24} height={24} />
        </ListItemButton>
      </ListItem>
    ))}
  </List>
);

export const InstallsLocationsDrawer: React.FC<InstallsLocationsDrawerProps> =
  React.memo(({ open, onClose, companyId, product, productLogo }) => {
    const installFilters = useCompanyPageStore((state) => state.installFilters);
    const [expandedCountries, setExpandedCountries] = useState<Set<string>>(
      new Set()
    );
    const [searchTerm, setSearchTerm] = useState("");
    const [visibleStatesPerCountry, setVisibleStatesPerCountry] = useState<
      Map<string, number>
    >(new Map());

    // First query to get valid countries
    const {
      data: countriesData,
      loading: loadingCountries,
      error: errorCountries,
    } = useQuery<CompanyInstallsPayload>(
      COMPANY_INSTALL_LOCATIONS_BY_PRODUCT_ID_QUERY,
      {
        variables: {
          hgId: companyId,
          per: 100000,
          countryAlpha2s: installFilters.countryAlpha2s,
          productIds: product?.id ? [product.id] : [],
        },
        context: {
          clientName: COMPANY_SEARCH_CLIENT,
        },
        skip: !open || !companyId || !product?.id,
        fetchPolicy: "no-cache",
      }
    );

    // Extract country codes from first query
    const validCountryCodes = useMemo(() => {
      if (!countriesData?.install?.data) return [];

      const countrySet = new Set<string>();
      countriesData.install.data.forEach((install) => {
        const countryCode = install.location?.state?.country?.alpha2;
        if (countryCode) {
          countrySet.add(countryCode);
        }
      });

      return Array.from(countrySet);
    }, [countriesData]);

    // Second query to get detailed locations using the valid countries
    const { data, loading, error } = useQuery<CompanyInstallsPayload>(
      COMPANY_INSTALLS_LOCATIONS_QUERY,
      {
        variables: {
          hgId: companyId,
          per: 100000,
          countryAlpha2s: validCountryCodes,
          productIds: product?.id ? [product.id] : [],
        },
        context: {
          clientName: COMPANY_SEARCH_CLIENT,
        },
        skip:
          !open || !companyId || !product?.id || validCountryCodes.length === 0,
        fetchPolicy: "no-cache",
      }
    );

    const countries = useMemo(() => {
      const installs = data?.install?.data || [];
      const countryMap = new Map<string, CountryData>();

      installs.forEach((install) => {
        const country = install.location?.state?.country;
        const state = install.location?.state;
        const city = install.location?.city;

        if (country?.alpha2 && country?.name) {
          const countryKey = country.alpha2;
          let countryData = countryMap.get(countryKey);

          if (!countryData) {
            countryData = {
              countryCode: country.alpha2,
              countryName: country.name,
              totalLocations: 0,
              states: [],
            };
            countryMap.set(countryKey, countryData);
          }

          countryData.totalLocations += install.locations || 0;

          const stateName = state?.name || "";
          const stateData: StateData = {
            stateName,
            cityName: city,
            locations: install.locations || 0,
          };
          countryData.states.push(stateData);
        }
      });

      // Process and deduplicate states within each country
      countryMap.forEach((countryData) => {
        const stateMap = new Map<string, StateData>();

        countryData.states.forEach((state) => {
          const stateKey = `${state.stateName}|${state.cityName}`;
          const existing = stateMap.get(stateKey);

          if (existing) {
            existing.locations += state.locations;
          } else {
            stateMap.set(stateKey, { ...state });
          }
        });

        countryData.states = Array.from(stateMap.values()).sort(
          (a, b) => b.locations - a.locations
        );
      });

      return Array.from(countryMap.values()).sort(
        (a, b) => b.totalLocations - a.totalLocations
      );
    }, [data]);

    const filteredCountries = useMemo(() => {
      if (!searchTerm.trim()) return countries;

      const searchLower = searchTerm.toLowerCase();
      const filtered: CountryData[] = [];

      countries.forEach((country) => {
        const countryMatches = country.countryName
          .toLowerCase()
          .includes(searchLower);
        const matchingStates = country.states.filter((state) => {
          const stateName = state.stateName.toLowerCase();
          const cityName = state.cityName?.toLowerCase() || "";
          return (
            stateName.includes(searchLower) || cityName.includes(searchLower)
          );
        });

        if (countryMatches) {
          // If country matches, include all states
          filtered.push(country);
        } else if (matchingStates.length > 0) {
          // If only states match, include country with filtered states
          filtered.push({
            ...country,
            states: matchingStates,
          });
        }
      });

      return filtered;
    }, [countries, searchTerm]);

    // Auto-expand countries when search finds states
    useEffect(() => {
      if (searchTerm.trim()) {
        const autoExpandCountries = new Set<string>();

        filteredCountries.forEach((country) => {
          const hasStateMatches = country.states.some((state) => {
            const stateName = state.stateName.toLowerCase();
            const cityName = state.cityName?.toLowerCase() || "";
            const searchLower = searchTerm.toLowerCase();
            return (
              stateName.includes(searchLower) || cityName.includes(searchLower)
            );
          });

          const countryMatches = country.countryName
            .toLowerCase()
            .includes(searchTerm.toLowerCase());

          if (hasStateMatches && !countryMatches) {
            autoExpandCountries.add(country.countryCode);
          }
        });

        if (autoExpandCountries.size > 0) {
          setExpandedCountries(
            (prev) => new Set([...prev, ...autoExpandCountries])
          );
        }
      }
    }, [searchTerm, filteredCountries]);

    // Auto-expand when there's only 1 country
    useEffect(() => {
      if (filteredCountries.length === 1 && !searchTerm.trim()) {
        const [singleCountry] = filteredCountries;
        setExpandedCountries(
          (prev) => new Set([...prev, singleCountry.countryCode])
        );
      }
    }, [filteredCountries, searchTerm]);

    const handleToggleCountry = useCallback((countryCode: string) => {
      setExpandedCountries((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(countryCode)) {
          newSet.delete(countryCode);
        } else {
          newSet.add(countryCode);
        }
        return newSet;
      });
    }, []);

    const handleShowMoreStates = useCallback((countryCode: string) => {
      setVisibleStatesPerCountry((prev) => {
        const newMap = new Map(prev);
        const current = newMap.get(countryCode) || 30;
        newMap.set(countryCode, current + 30);
        return newMap;
      });
    }, []);

    const getVisibleStatesCount = useCallback(
      (countryCode: string) => {
        return visibleStatesPerCountry.get(countryCode) || 30;
      },
      [visibleStatesPerCountry]
    );

    const handleClearSearch = useCallback(() => {
      setSearchTerm("");
    }, []);

    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{
          paper: { sx: { width: 350, maxWidth: "md" } },
        }}
      >
        <Stack direction="column" height="100%">
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
            padding={2}
          >
            <Box>
              <Typography
                color="text.secondary"
                variant="h5"
                sx={{ textTransform: "uppercase", letterSpacing: 1 }}
              >
                Product Install Locations
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>

          {/* Product Info */}
          {product && (
            <Box sx={{ px: 2, mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                <CompanyLogo
                  domain={productLogo}
                  size={32}
                  variant="rounded"
                  fallback={<Settings />}
                />
                <Box flex={1}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    color="text.primary"
                    sx={{ mb: 0.5 }}
                  >
                    {product.name}
                  </Typography>
                </Box>
                <Chip
                  label={`${filteredCountries.length} Countries`}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontWeight: "medium",
                    color: "primary.main",
                    borderColor: "primary.main",
                  }}
                />
              </Stack>
            </Box>
          )}

          {/* Search */}
          <Box sx={{ px: 2, mb: 2 }}>
            <TextField
              fullWidth
              variant="standard"
              placeholder="Search countries, states, or cities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        sx={{ p: 0.5 }}
                      >
                        <ClearIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                "& .MuiInput-underline:before": {
                  borderBottomColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.2),
                },
                "& .MuiInput-underline:hover:before": {
                  borderBottomColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.4),
                },
              }}
            />
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: "auto", ...lightScrollbarStyle }}>
            {loadingCountries || loading ? (
              <LoadingSkeleton />
            ) : errorCountries || error ? (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="error">
                  Failed to load location data
                </Typography>
              </Box>
            ) : filteredCountries.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  {searchTerm.trim()
                    ? `No results found for "${searchTerm}"`
                    : "No location data available"}
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {filteredCountries.map((country) => (
                  <CountryListItem
                    key={country.countryCode}
                    country={country}
                    isExpanded={expandedCountries.has(country.countryCode)}
                    onToggle={() => handleToggleCountry(country.countryCode)}
                    visibleStatesCount={getVisibleStatesCount(
                      country.countryCode
                    )}
                    onShowMoreStates={() =>
                      handleShowMoreStates(country.countryCode)
                    }
                  />
                ))}
              </List>
            )}
          </Box>
        </Stack>
      </Drawer>
    );
  });

InstallsLocationsDrawer.displayName = "InstallsLocationsDrawer";
